import React, { useState, useEffect } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { propTypes } from '../../../../util/types';
import { useLocation } from 'react-router-dom';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  AvatarMedium,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';

import css from './TopbarDesktop.module.css';
import { getUserDisplayInfo, getUserRole } from '../../../../util/helper';
import Modal from '../../../../components/Modal/Modal';
import LanguageSwitcher from '../../../../components/LanguageSwitcher/LanguageSwitcher';
import UserSendInvitatinPage from '../../../UserSendInvitation/UserSendInvitatinPage';
import { Toaster } from 'react-hot-toast';
import profileImage from '../../../../assets/profile-image.png';

const onManageDisableScrolling = () => {
  // console.log("onManageDisableScrolling");
};

const TopbarDesktop = props => {
  const {
    className,
    appConfig,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    listingNotifications,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
  } = props;

  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const location = useLocation();
  const userRole = currentUser && getUserRole(currentUser);
  const isCustomer = userRole === 'customer';
  const unreadNotifications = listingNotifications || [];
  const unreadCount = unreadNotifications.length;
  const isPublished = currentUser?.attributes?.profile?.publicData?.listingPublish;

  const marketplaceName = appConfig.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(rootClassName || css.root, className);
  const { fullName, email, imageUrl } = getUserDisplayInfo(currentUser);

  const search = (
    <TopbarSearchForm
      className={css.searchLink}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={appConfig}
    />
  );

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const inboxLink = authenticatedOnClientSide ? (
    <NamedLink
      className={css.inboxLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.inbox}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  ) : null;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const profileMenu = authenticatedOnClientSide ? (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <AvatarMedium className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="profileHeader" className={css.profileBox}>
          <NamedLink className={css.profileLink} name="ProfileSettingsPage">
            <div className={css.profileCover}>
              <div className={css.profileName}>
                {' '}
                {fullName || currentUser?.attributes?.profile?.displayName || 'User'}
              </div>
              <div className={css.profileEmail}>{email || 'No email'}</div>
            </div>
            <div className={css.profileImage}>
              <img
                src={imageUrl || profileImage}
                alt={fullName ? `${fullName} profile` : 'profile-image'}
              />
            </div>
          </NamedLink>
        </MenuItem>
        <MenuItem key="ManageListingsPage">
          {userRole == 'photographer' && (
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          )}
        </MenuItem>
        <MenuItem key="PhotographerGuidePage">
          {userRole == 'photographer' && (
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('PhotographerGuidePage'))}
              name="PhotographerGuidePage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.photographerGuideLink" />
            </NamedLink>
          )}
        </MenuItem>
        <MenuItem key="PurchaseImagePage">
          {userRole == 'customer' && (
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('PurchaseImagePage'))}
              name="PurchaseImagePage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourImageGalleryLink" />
            </NamedLink>
          )}
        </MenuItem>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.profileSettingsLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(
              css.yourListingsLink,
              location?.pathname.includes('/account') && 'NamedLink_active',
              currentPageClass('AccountSettingsPage')
            )}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout" className={css.logoutMenuItem}>
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : null;

  const signupLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="SignupPage" className={css.signupLink}>
      <span className={css.signup}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );

  const loginLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="LoginPage" className={css.loginLink}>
      <span className={css.login}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );

  return (
    <div className={css.headerWrapper}>
      <nav className={classes}>
        <LinkedLogo
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        />
        {isAuthenticatedOrJustHydrated && (
          <div className={css.centerContent}>
            <a href="/p/about" className={css.menuLinks}>
              <FormattedMessage id="TopbarDesktop.aboutUs" />
            </a>
            <a href="/p/faq" className={css.menuLinks}>
              <FormattedMessage id="TopbarDesktop.faqs" />
            </a>
            <a href="/p/contact-us" className={css.menuLinks}>
              <FormattedMessage id="TopbarDesktop.contactUs" />
            </a>
          </div>
        )}
        <div className={css.linkWrapper}>
          <LanguageSwitcher />
          <NamedLink className={css.createListingLink} name="OnLocationPage">
            <span className={css.createListing}>
              <FormattedMessage id="TopbarDesktop.OnLocationPage" />
            </span>
          </NamedLink>
          {userRole == 'photographer' && (
            <NamedLink className={css.createListingLink} name="ShowCustomersPage">
              <span className={css.createListing}>
                <FormattedMessage id="TopbarDesktop.Customers" />
              </span>
            </NamedLink>
          )}
          {userRole == 'photographer' && (
            <NamedLink className={css.createListingLink} name="MySpotsPage">
              <span className={css.createListing}>
                <FormattedMessage id="TopbarDesktop.MySpots" />
              </span>
            </NamedLink>
          )}
          {userRole == 'photographer' && (
            <div className={css.createListingLink} onClick={() => setIsOpen(true)}>
              <span className={css.createListing}>
                <FormattedMessage id="TopbarDesktop.UserSendInvitation" />
              </span>
            </div>
          )}
          <Toaster position="top-center" reverseOrder={false} />

          {isCustomer && authenticatedOnClientSide && (
            <div className={css.notificationBellWrapper}>
              <Menu>
                <MenuLabel className={css.bellMenuLabel} isOpenClassName={css.bellMenuOpen}>
                  <div className={css.bellIconWrapper}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                    </svg>
                    {unreadCount > 0 && (
                      <span className={css.bellBadge}>{unreadCount}</span>
                    )}
                  </div>
                </MenuLabel>
                <MenuContent className={css.bellMenuContent}>
                  {unreadCount === 0 ? (
                    <MenuItem key="empty">
                      <div className={css.bellEmptyState}>
                        <FormattedMessage id="Notifications.empty" />
                      </div>
                    </MenuItem>
                  ) : (
                    unreadNotifications.map(n => (
                      <MenuItem key={n.id}>
                        <NamedLink
                          className={css.bellNotificationItem}
                          name="ListingPage"
                          params={{ id: n.listingId, slug: n.listingSlug || n.listingId }}
                        >
                          <span className={css.bellNotificationDot} />
                          <span>
                            <FormattedMessage
                              id={n.listingTitle ? 'Notifications.photosReady' : 'Notifications.photosReadyNoTitle'}
                              values={{ title: <b>{n.listingTitle}</b> }}
                            />
                          </span>
                        </NamedLink>
                      </MenuItem>
                    ))
                  )}
                </MenuContent>
              </Menu>
            </div>
          )}

          {inboxLink}
          {profileMenu}
          {loginLink}
          {signupLink}
        </div>
      </nav>
      <Modal
        id="userSendInvitation"
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        className={css.UserSendInvitatinPage}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <UserSendInvitatinPage setUserSendInvitationMobile={setIsOpen} />
      </Modal>
    </div>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  appConfig: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  appConfig: object,
};

export default TopbarDesktop;
